/**
 * Página da folha ↔ linha da grade (cara-crachá V1, spec 2026-09-11 §7 item 1).
 *
 * Sem OCR e sem detecção automática: a página é o que o operador marcou (ou o que o
 * backend já tinha em `pageNumber`). Estes helpers só traduzem entre as duas visões.
 */

/** O mínimo que uma linha precisa expor para participar do mapa. */
export interface PageMappedRow {
  pageNumber?: number | null
}

/** Texto da célula "Folha" — travessão quando a linha ainda não tem página marcada. */
export function formatPageCell(page: number | null | undefined): string {
  return isValidPage(page) ? String(page) : '—'
}

/** Página é inteiro positivo. `0`, negativo, fracionário e `NaN` não são página. */
export function isValidPage(page: number | null | undefined): page is number {
  return typeof page === 'number' && Number.isInteger(page) && page >= 1
}

/** Página da linha, normalizada — `null` quando a linha não tem página utilizável. */
export function resolveRowPage(row: PageMappedRow | null | undefined): number | null {
  if (!row) return null
  return isValidPage(row.pageNumber) ? row.pageNumber : null
}

/**
 * Trava a página no intervalo do documento. `totalPages` desconhecido (`0`/ausente) mantém
 * a página pedida: o PDF pode não ter carregado ainda quando a linha é clicada.
 */
export function clampPage(page: number, totalPages?: number | null): number {
  const target = Number.isFinite(page) ? Math.trunc(page) : 1
  if (!totalPages || totalPages < 1) return Math.max(1, target)
  return Math.min(Math.max(1, target), totalPages)
}

/** Índices (0-based) das linhas de cada página, na ordem da grade. */
export function buildPageRowIndex(rows: PageMappedRow[]): Map<number, number[]> {
  const index = new Map<number, number[]>()
  rows.forEach((row, position) => {
    const page = resolveRowPage(row)
    if (page === null) return
    const bucket = index.get(page)
    if (bucket) bucket.push(position)
    else index.set(page, [position])
  })
  return index
}

/** Índices das linhas marcadas na página informada. */
export function rowsOnPage(rows: PageMappedRow[], page: number | null | undefined): number[] {
  if (!isValidPage(page)) return []
  return buildPageRowIndex(rows).get(page) ?? []
}

/** Quantas linhas ainda não têm página marcada — alimenta o contador do cabeçalho. */
export function countRowsWithoutPage(rows: PageMappedRow[]): number {
  return rows.filter((row) => resolveRowPage(row) === null).length
}
